// Updated ObjectDetailsPanel.jsx - With real-time filter functionality
import React, { useState, useMemo } from 'react';
import FlexiblePlot from '../../components/charts/FlexiblePlot';

const ObjectDetailsPanel = ({ selectedObject, setSelectedObject, showSearch, showConeSearch }) => {
  const [filterText, setFilterText] = useState('');
  
  // Download object data as JSON
  const downloadObjectData = () => {
    if (!selectedObject) return;
    
    try {
      // Create clean data object for download
      const downloadData = {
        type: selectedObject.__type,
        coordinates: {
          ra: selectedObject.__ra || selectedObject.ra,
          dec: selectedObject.__dec || selectedObject.dec,
        },
        catalog: selectedObject.__catalog_name || 'Unknown',
        search_radius: selectedObject.__search_radius,
        timestamp: new Date().toISOString(),
        data: {}
      };
      
      // Add all object properties (excluding internal __properties)
      Object.entries(selectedObject).forEach(([key, value]) => {
        if (!key.startsWith('__') && key !== 'ra' && key !== 'dec' && key !== 'note') {
          downloadData.data[key] = value;
        }
      });
      
      // Add note if exists
      if (selectedObject.note) {
        downloadData.note = selectedObject.note;
      }
      
      // Create and download file
      const jsonString = JSON.stringify(downloadData, null, 2);
      const blob = new Blob([jsonString], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      
      // Generate filename based on object type and coordinates
      const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
      let filename;
      
      if (selectedObject.__type === 'catalog') {
        const ra = (selectedObject.__ra || selectedObject.ra || 0).toFixed(6);
        const dec = (selectedObject.__dec || selectedObject.dec || 0).toFixed(6);
        const catalog = (selectedObject.__catalog_name || 'catalog').replace(/\./g, '_');
        filename = `${catalog}_object_${ra}_${dec}_${timestamp}.json`;
      } else {
        const ra = (selectedObject.ra || 0).toFixed(6);
        const dec = (selectedObject.dec || 0).toFixed(6);
        filename = `sky_position_${ra}_${dec}_${timestamp}.json`;
      }
      
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      console.log('Downloaded object data:', downloadData);
    } catch (error) {
      console.error('Error downloading object data:', error);
    }
  };

  // Download object data as CSV (for catalog objects)
  const downloadObjectCSV = () => {
    if (!selectedObject || selectedObject.__type !== 'catalog') return;
    
    try {
      // Get all data fields
      const dataFields = {};
      Object.entries(selectedObject).forEach(([key, value]) => {
        if (!key.startsWith('__') && key !== 'ra' && key !== 'dec' && key !== 'note') {
          dataFields[key] = value;
        }
      });
      
      // Create CSV content
      const headers = ['ra', 'dec', 'catalog', ...Object.keys(dataFields)];
      const values = [
        selectedObject.__ra || selectedObject.ra || '',
        selectedObject.__dec || selectedObject.dec || '',
        selectedObject.__catalog_name || '',
        ...Object.values(dataFields)
      ];
      
      const csvContent = [
        headers.join(','),
        values.map(val => `"${val}"`).join(',')
      ].join('\n');
      
      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      
      const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
      const ra = (selectedObject.__ra || selectedObject.ra || 0).toFixed(6);
      const dec = (selectedObject.__dec || selectedObject.dec || 0).toFixed(6);
      const catalog = (selectedObject.__catalog_name || 'catalog').replace(/\./g, '_');
      const filename = `${catalog}_object_${ra}_${dec}_${timestamp}.csv`;
      
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      console.log('Downloaded CSV data');
    } catch (error) {
      console.error('Error downloading CSV data:', error);
    }
  };

  // Filter object attributes in real-time
  const filteredAttributes = useMemo(() => {
    if (!selectedObject || selectedObject.__type !== 'catalog') return [];
    
    const attributes = Object.entries(selectedObject).filter(([key]) => 
      !key.startsWith('__') && key !== 'ra' && key !== 'dec' && key !== 'note'
    );
    
    if (!filterText.trim()) return attributes;
    
    const searchTerm = filterText.toLowerCase();
    return attributes.filter(([key, value]) => 
      key.toLowerCase().includes(searchTerm) || 
      String(value).toLowerCase().includes(searchTerm)
    );
  }, [selectedObject, filterText]);

  // Clear filter when object changes
  React.useEffect(() => {
    setFilterText('');
  }, [selectedObject]);

  if (!selectedObject) return null;

  // Calculate margins based on panel visibility
  const getMarginStyles = () => {
    let marginLeft = '1.5rem';
    let marginRight = '1.5rem';

    if (showSearch) {
      marginLeft = '22rem';
    }

    if (showConeSearch) {
      marginRight = '25rem';
    }

    return { marginLeft, marginRight };
  };

  return (
    <div
      className="absolute bottom-6 left-6 right-6 z-10"
      style={getMarginStyles()}
    >
      <div className="bg-ui-light-card/90 dark:bg-ui-dark-card/90 backdrop-blur-sm border border-ui-light-border/60 dark:border-ui-dark-border/60 rounded-lg shadow-md p-4 text-ui-light-text-primary dark:text-ui-dark-text-primary max-h-[600px] overflow-auto transition-all duration-300">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium">Object Details</h3>
          
          <div className="flex items-center space-x-2">
            {/* Download Buttons */}
            <div className="flex items-center space-x-1">
              <button
                onClick={downloadObjectData}
                className="flex items-center space-x-1 px-2 py-1 text-xs bg-primary-600/80 hover:bg-primary-700 text-white rounded transition-colors"
                title="Download as JSON"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <span>JSON</span>
              </button>
              
              {selectedObject.__type === 'catalog' && (
                <button
                  onClick={downloadObjectCSV}
                  className="flex items-center space-x-1 px-2 py-1 text-xs bg-green-600/80 hover:bg-green-700 text-white rounded transition-colors"
                  title="Download as CSV"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <span>CSV</span>
                </button>
              )}
            </div>
            
            {/* Close Button */}
            <button
              onClick={() => setSelectedObject(null)}
              className="text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary hover:text-ui-light-text-primary dark:hover:text-ui-dark-text-primary transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
        
        {selectedObject.__type === 'catalog' ? (
          <div className="text-sm">
            <div className="mb-3">
              <span className="inline-block px-2 py-0.5 text-xs rounded bg-status-error-light-bg dark:bg-status-error-dark-bg text-status-error-light-text dark:text-status-error-dark-text mr-2 font-medium">
                Catalog Object
              </span>
              <span className="text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary">
                RA: {Number(selectedObject.__ra).toFixed(6)}° • Dec: {Number(selectedObject.__dec).toFixed(6)}°
              </span>
              {selectedObject.__catalog_name && (
                <span className="text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary ml-2">
                  • {selectedObject.__catalog_name}
                </span>
              )}
            </div>

            {/* Filter Input - Only for catalog objects */}
            <div className="mb-3">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Filter attributes..."
                  value={filterText}
                  onChange={(e) => setFilterText(e.target.value)}
                  className="w-full px-3 py-1 text-xs bg-ui-light-background/60 dark:bg-ui-dark-background/60 border border-ui-light-border/40 dark:border-ui-dark-border/40 rounded text-ui-light-text-primary dark:text-ui-dark-text-primary placeholder-ui-light-text-tertiary/70 dark:placeholder-ui-dark-text-tertiary/70 focus:outline-none focus:ring-1 focus:ring-primary-500/50 focus:border-primary-500/50 transition-all"
                />
                <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                  {filterText ? (
                    <button
                      onClick={() => setFilterText('')}
                      className="text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary hover:text-ui-light-text-secondary dark:hover:text-ui-dark-text-secondary"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  ) : (
                    <svg className="w-3 h-3 text-ui-light-text-tertiary/50 dark:text-ui-dark-text-tertiary/50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  )}
                </div>
              </div>
              {filterText && (
                <p className="text-xs text-ui-light-text-tertiary/80 dark:text-ui-dark-text-tertiary/80 mt-1">
                  Showing {filteredAttributes.length} of {Object.keys(selectedObject).filter(k => !k.startsWith('__') && k !== 'ra' && k !== 'dec' && k !== 'note').length} attributes
                </p>
              )}
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1 max-h-24 overflow-auto">
              {filteredAttributes.length > 0 ? (
                filteredAttributes.map(([k, v]) => (
                  <div key={k} className="text-xs">
                    <span className="font-medium text-ui-light-text-secondary dark:text-ui-dark-text-secondary">{k}:</span>{' '}
                    <span className="text-ui-light-text-primary dark:text-ui-dark-text-primary">{String(v)}</span>
                  </div>
                ))
              ) : filterText ? (
                <div className="col-span-full text-xs text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary italic">
                  No attributes match "{filterText}"
                </div>
              ) : null}
            </div>
          </div>
        ) : (
          <div className="text-sm">
            <div className="mb-2">
              <span className="inline-block px-2 py-0.5 text-xs rounded bg-status-warning-light-bg dark:bg-status-warning-dark-bg text-status-warning-light-text dark:text-status-warning-dark-text mr-2 font-medium">
                Sky Position
              </span>
              <span className="text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary">
                RA: {Number(selectedObject.ra).toFixed(6)}° • Dec: {Number(selectedObject.dec).toFixed(6)}°
              </span>
            </div>
            {selectedObject.note && (
              <p className="text-ui-light-text-secondary dark:text-ui-dark-text-secondary">{selectedObject.note}</p>
            )}
          </div>
        )}
        
        {/* Download info for catalog objects */}
        {selectedObject.__type === 'catalog' && (
          <div className="mt-2 pt-2 border-t border-ui-light-border/30 dark:border-ui-dark-border/30">
            <p className="text-xs text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary">
              Download options: JSON (complete data) • CSV (tabular format)
            </p>
          </div>
        )}

        {/* FlexiblePlot component for catalog objects */}
        {selectedObject.__type === 'catalog' && selectedObject.__metadata && (
          <div className="mt-4 pt-4 border-t border-ui-light-border/30 dark:border-ui-dark-border/30">
            <FlexiblePlot
              objectData={(() => {
                // Create a clean object without the __ prefixed properties
                const cleanObject = {};
                Object.keys(selectedObject).forEach(key => {
                  if (!key.startsWith('__')) {
                    cleanObject[key] = selectedObject[key];
                  }
                });
                return cleanObject;
              })()}
              metadata={selectedObject.__metadata}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default ObjectDetailsPanel;